import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "auto-dealer-web-design-colorado",
  metaTitle: "Auto Dealer Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom auto dealer web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Auto dealer marketing", to: "/auto-dealer-marketing-colorado" },
  ],
  currentLabel: "Auto Dealer Web Design Colorado",
  eyebrow: "Auto Dealer Web Design — Colorado",
  h1: (
    <>
      Auto Dealer <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded auto dealer websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Auto Dealer websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first auto dealer sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Auto Dealer web design in Colorado",
    body: "Auto Dealer web design is the practice of building conversion-focused, mobile-first websites for auto dealer companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Fast custom site that wraps your inventory feed without the bloat of off-the-shelf dealer platforms." },
    { title: "VDP (Vehicle Detail Pages) that rank", body: "Properly structured VDPs with Vehicle schema, image galleries, and lead forms." },
    { title: "Inventory feed integration", body: "Live integration with HomeNet, vAuto, or your DMS — inventory updates without manual work." },
    { title: "Trade-in & finance forms", body: "Conversion-tested forms with VIN-decode and pre-qualification logic." },
    { title: "Service department pages", body: "Dedicated service, parts, and body shop pages that rank independently from sales." },
    { title: "Schema and SEO at launch", body: "AutoDealer, Vehicle, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Dealer platforms are slow and locked", body: "Most dealer sites are on DealerOn, Dealer.com, or DealerInspire — fast on demo, slow in production, no SEO control." },
    { heading: "VDPs are the ranking opportunity", body: "Each vehicle is a unique URL that can rank for year/make/model + city queries — most dealers waste this." },
    { heading: "Trade-in leads convert", body: "Trade-in forms convert 3x higher than \"contact us\" — we put them on every page." },
  ],
  relatedLinks: [
    { to: "/auto-dealer-marketing-colorado", label: "Auto dealer marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado auto dealer case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a auto dealer website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a auto dealer website?", a: "Typical timeline is 4–6 weeks for a 12–20 page auto dealer site with full service and service-area pages. Larger multi-location auto dealer companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs auto dealer companies?", a: "No — we build auto dealer sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Auto Dealer Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for auto dealer companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/auto-dealer-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
